import React from "react";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Dock from "./Dock";

const pages = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export default function Footer() {
  const [isHoveringFooter, setIsHoveringFooter] = React.useState(false);

  const socialItems = [
    {
      icon: <Facebook className="h-5 w-5" />,
      label: "Facebook",
      href: "https://www.facebook.com/share/1NfUfrymZj/",
      hoverBgClassName: "bg-blue-500",
    },
    {
      icon: <Instagram className="h-5 w-5" />,
      label: "Instagram",
      href: "https://www.instagram.com/holaworldaustralia?igsh=YnUwbmU1NWJwNTF3",
      hoverBgClassName: "bg-gradient-to-bl from-purple-500 via-pink-500 to-yellow-500",
    },
    {
      icon: <Youtube className="h-5 w-5" />,
      label: "YouTube",
      href: "https://youtube.com/@teamholaworldenglish?si=lrjGd11Xh2W_Wj4k",
      hoverBgClassName: "bg-[#FF3000]",
    },
    {
      icon: <Linkedin className="h-5 w-5" />,
      label: "LinkedIn",
      href: "https://www.linkedin.com/company/hola-world/",
      hoverBgClassName: "bg-[#006699]",
    },
  ];

  return (
    <section
      className="bg-[#ECEFF3] mt-0 pb-[36px] overflow-hidden"
      style={{
        fontFamily: "Inter, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif",
        letterSpacing: "-0.2px",
      }}
    >
      <div
        className="w-full bg-[#ECEFF3] px-3 sm:px-6 lg:px-8 pt-3 sm:pt-4 lg:pt-6 pb-0 overflow-hidden"
        onMouseEnter={() => setIsHoveringFooter(true)}
        onMouseLeave={() => setIsHoveringFooter(false)}
      >
          <footer className="rounded-[40px] border border-[#E6E8EC] bg-[#F6F7F8] px-8 lg:px-[60px] py-14">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_0.7fr_0.8fr] gap-y-10 lg:gap-y-0 lg:gap-x-16 items-start">
              <div>
                <div className="h-16 w-auto">
                  <img src="/logo.png" alt="Hola World logo" className="h-16 w-auto" />
                </div>

                <div className="mt-14">
                  <Dock items={socialItems} />
                </div>
              </div>

              <div className="flex lg:justify-start">
                <div>
                  <h4 className="text-[28px] lg:text-[30px] font-bold text-[#1F2328] mb-6">Pages</h4>
                  <ul className="space-y-[14px]">
                    {pages.map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.href}
                          className="text-[14px] leading-[1.7] font-normal text-[#6B6B6B] hover:text-[#555] transition-colors duration-200"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="lg:justify-self-start">
                <h4 className="text-[28px] lg:text-[30px] font-bold text-[#1F2328] mb-6">Company</h4>
                <ul className="text-[14px] leading-[1.7] text-[#6B6B6B] space-y-[14px] max-w-[320px]">
                  <li className="flex items-start gap-2">
                    <MapPin className="h-[15px] w-[15px] mt-1 shrink-0 text-[#6B6B6B]" />
                    <span>
                      Level 14, 3 Parramatta Square,
                      <br />
                      153 Macquarie St,
                      <br />
                      Parramatta NSW 2150, Australia
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Phone className="h-[15px] w-[15px] mt-1 shrink-0 text-[#6B6B6B]" />
                    <span>
                      +61 475 752 510
                      <br />
                      +91 80895 79615
                      <br />
                      +91 90376 62324
                      <br />
                      1800 80 4652
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="h-[15px] w-[15px] mt-1 shrink-0 text-[#6B6B6B]" />
                    <span>hello@holaworld.in</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-7 mb-4 h-px bg-[#E5E5E5]" />

            <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-[13px] text-[#8A8A8A]">
              <p>© 2026 Hola World Migration. All rights reserved.</p>
              <div className="flex flex-wrap items-center justify-center gap-6">
                <Link to="/privacy-policy" className="hover:text-[#666] transition-colors duration-200">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="hover:text-[#666] transition-colors duration-200">
                  Terms of Service
                </Link>
                <Link to="/cookie-settings" className="hover:text-[#666] transition-colors duration-200">
                  Cookies Settings
                </Link>
              </div>
            </div>
          </footer>

          <motion.div
            aria-hidden="true"
            className="pointer-events-none select-none hidden sm:block mt-3 w-full text-center whitespace-nowrap font-extrabold leading-none text-black"
            initial={{ rotateX: 0, rotateY: 0, y: 0 }}
            animate={
              isHoveringFooter
                ? { rotateX: 10, rotateY: -6, y: -6 }
                : { rotateX: 0, rotateY: 0, y: 0 }
            }
            transition={{ duration: 0.35, ease: "easeOut" }}
            style={{
              fontSize: "clamp(180px, 14vw, 280px)",
              opacity: 0.06,
              letterSpacing: "-3px",
              transformStyle: "preserve-3d",
              transformPerspective: 1200,
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.88) 65%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.88) 65%, rgba(0,0,0,0) 100%)",
            }}
          >
            Hola World
          </motion.div>
      </div>
    </section>
  );
}
